import { api } from '../api';

import type { UserType } from 'api/api.types';

export type UsersResponse = UserType[];

export type UserFilterType =
  | 'active'
  | 'deactivated'
  | 'pending'
  | 'admins'
  | 'curators'
  | 'consumers'
  | 'suppressed';

export type UserCountsResponse = Record<UserFilterType | 'all', number>;

export type UsersQueryParams = {
  keep?: string; // comma separated values: kluebot, deleted, fallback
  full?: 1;
  limit?: number;
  page?: number;
  order?:
    'alpha' | 'alpha-rev' | 'created' | 'created-rev' | 'login' | 'login-rev';
  typeFilter?: UserFilterType;
  groupFilter?: string; // visibility group ID, "0" for "full access" users
  query?: string; // search string for at_name, first_name, last_name, email
};

export type UserUpdateOptions = {
  id: number;
  addRoles?: string[];
  removeRoles?: string[];
  transferAdminId?: number;
  undo?: 'y';
};

export type InviteSuccessResponse = {
  id: number;
  email: string;
};

export type InviteErrorResponse = {
  message: string;
  email: string;
};

export type EndedSessionsResponse = {
  sessions: number;
  tokens: number;
};

export type PasswordActionResponse = {
  message: string;
};

const injectedApi = api.injectEndpoints({
  endpoints: (build) => ({
    getUsers: build.query<UsersResponse, UsersQueryParams | void>({
      query: (params) => ({
        url: '/users.json',
        method: 'GET',
        params: {
          page: params?.page || 1,
          limit: params?.limit || 50,
          order: params?.order || 'alpha',
          typeFilter: params?.typeFilter || 'active',
          groupFilter: params?.groupFilter,
          query: params?.query || '',
          full: '1',
          keep: params?.keep || '',
        },
      }),
      providesTags: ['Users'],
    }),

    getUserById: build.query<UserType, string | number>({
      query: (id) => ({
        url: `/users/${id}.json`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'Users', id }],
    }),

    getCurrentUser: build.query<UserType, void>({
      query: () => ({
        url: '/users/me.json',
        method: 'GET',
      }),
      providesTags: ['CurrentUser'],
    }),

    getUserCounts: build.query<
      UserCountsResponse,
      { query?: string; groupFilter?: string }
    >({
      query: (params = {}) => ({
        url: '/users/counts.json',
        method: 'GET',
        params,
      }),
      providesTags: ['UserCounts'],
    }),

    updateUser: build.mutation<UserType, UserUpdateOptions>({
      query: (options) => ({
        url: `/users/${options.id}.json`,
        method: 'PUT',
        body: {
          addRoles: options.addRoles,
          removeRoles: options.removeRoles,
          transferAdminId: options.transferAdminId,
        },
      }),
      invalidatesTags: ['Users', 'UserCounts'],
    }),

    deleteUser: build.mutation<UserType, UserUpdateOptions>({
      query: (options) => ({
        url: `/users/${options.id}.json`,
        method: 'DELETE',
        body: {
          transferAdminId: options.transferAdminId,
          undo: options.undo,
        },
      }),
      invalidatesTags: ['Users', 'UserCounts'],
    }),

    inviteUser: build.mutation<
      InviteSuccessResponse,
      { email: string; dryRun?: string }
    >({
      query: ({ email, dryRun }) => {
        return {
          url: '/users/invite',
          method: 'POST',
          body: {
            email,
            ...(dryRun && { dryRun }),
          },
        };
      },
      invalidatesTags: ['Users', 'UserCounts'],
    }),

    resendInvitation: build.mutation<{ success: boolean }, { id: number }>({
      query: ({ id }) => ({
        url: `/users/${id}/reconfirm`,
        method: 'PUT',
      }),
      invalidatesTags: ['Users'],
    }),

    sendPasswordResetEmail: build.mutation<
      PasswordActionResponse,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/users/${id}/password_reset`,
        method: 'POST',
      }),
    }),

    // Ends every session the user has and revokes the app tokens they authorized,
    // without deactivating them. Admins only (ASVS V7.4.5).
    signOutUserEverywhere: build.mutation<
      EndedSessionsResponse,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/users/${id}/sessions`,
        method: 'DELETE',
      }),
    }),

    // Emails the signed-in user a secure link to set/reset their password.
    // Passwords are never set in-app, so a leaked session cookie can't be used
    // to plant a password without access to the user's inbox.
    resetCurrentUserPassword: build.mutation<PasswordActionResponse, void>({
      query: () => ({
        url: '/account/password_reset',
        method: 'POST',
      }),
    }),
  }),
});

export const {
  useGetUsersQuery,
  useLazyGetUsersQuery,
  useGetUserByIdQuery,
  useGetCurrentUserQuery,
  // User Management (admin)
  useGetUserCountsQuery,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useInviteUserMutation,
  useResendInvitationMutation,
  useSendPasswordResetEmailMutation,
  useSignOutUserEverywhereMutation,
  useResetCurrentUserPasswordMutation,
} = injectedApi;
